/**
 * Whether the browser believes it has a network, and what the outbox is
 * holding, as one value for the shell to render.
 *
 * `navigator.onLine` is a coarse signal (it says "no network", never "the
 * server is reachable"), which is exactly right for a banner: the app should
 * say it is offline only when the browser is sure. The outbox half is the
 * observable the queue and the flusher publish; its `version` field is
 * bookkeeping for live lists and is deliberately not exposed here.
 */

import { useCallback, useSyncExternalStore } from 'react';

import { flush, getOutboxState, subscribeToOutboxState } from '@shared/offline';

interface OfflineStatus {
  /** False when the browser reports no network. */
  online: boolean;
  /** Writes waiting to reach the server, failures excluded. */
  pendingCount: number;
  /** Writes the server refused, waiting for a Retry or a Discard. */
  failedCount: number;
  /** A replay hit a 401: the queue is intact but needs a signed-in session. */
  needsSignIn: boolean;
  /** This browser has no Web Locks, so queued writes will never replay here. */
  locksUnavailable: boolean;
  /** A replay is running right now. */
  syncing: boolean;
  /** When the last replay finished, or null if none has. */
  lastSyncAt: number | null;
  /** Ask for a replay now, the way a button would. */
  syncNow: () => void;
}

/** Re-read the browser's state whenever it announces a change. */
function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/** The browser's current belief; optimistic where there is no navigator at all. */
function getSnapshot(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

/** Subscribe to the browser's online/offline state and to the outbox. */
export function useOfflineStatus(): OfflineStatus {
  const online = useSyncExternalStore(subscribe, getSnapshot, () => true);
  const outbox = useSyncExternalStore(subscribeToOutboxState, getOutboxState, getOutboxState);
  const syncNow = useCallback(() => void flush('manual'), []);
  return {
    online,
    pendingCount: outbox.pendingCount,
    failedCount: outbox.failedCount,
    needsSignIn: outbox.needsSignIn,
    locksUnavailable: outbox.locksUnavailable,
    syncing: outbox.syncing,
    lastSyncAt: outbox.lastSyncAt,
    syncNow,
  };
}
